import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    Switch,
    FormControlLabel,
    Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import api from '../../api/api';
import en_ja from '../../translations/en_ja';

const DEFAULT_COORDS = { lat: 35.6762, lng: 139.6503 };

const AddStore = ({
    dialogOpen,
    onClose,
    editMode = false,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    storeData
}) => {
    const userDetails = JSON.parse(localStorage.getItem("user"));
    const [loading, setLoading] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState("");
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerInstance = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const geocoder = useRef(null);
    const [selectedPlaceId, setSelectedPlaceId] = useState(null);

    const [formData, setFormData] = useState({
        company_name: '',
        store_name: '',
        address: '',
        prefectures: '',
        postalCode: '',
        from: '',
        to: '',
        status: true,
        created_by: userDetails?.id,
        coordinates: DEFAULT_COORDS,
    });

    const [errors, setErrors] = useState({});

    const handleSelectPrediction = (placeId, description) => {
        setSelectedPlaceId(placeId);
        setFormData((prev) => ({ ...prev, address: description }));
        setPredictions([]);
        if (!placesService.current) return;
        placesService.current.getDetails({ placeId }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry && place.geometry.location) {
                const location = place.geometry.location;
                setFormData((prev) => ({
                    ...prev,
                    coordinates: { lat: location.lat(), lng: location.lng() },
                    address: place.formatted_address,
                }));
            }
        });
    };

    const loadGoogleMaps = () => {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps && window.google.maps.Map) {
                setMapLoaded(true);
                resolve();
                return;
            }
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                const checkGoogleMapsReady = () => {
                    if (window.google && window.google.maps && window.google.maps.Map) {
                        setMapLoaded(true);
                        resolve();
                    } else {
                        setTimeout(checkGoogleMapsReady, 100);
                    }
                };
                checkGoogleMapsReady();
                return;
            }
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD-206GuB2zh4Pl6c2EEf4c1U0Qw-ju9kY&libraries=places&callback=initGoogleMaps`;
            script.async = true;
            script.defer = true;
            window.initGoogleMaps = () => {
                if (window.google && window.google.maps && window.google.maps.Map) {
                    setMapLoaded(true);
                    resolve();
                } else {
                    setMapError(en_ja["Google Maps failed to initialize properly"]);
                    reject(new Error("Google Maps failed to initialize"));
                }
            };
            script.onerror = () => {
                setMapError(en_ja["Failed to load Google Maps script"]);
                reject(new Error("Failed to load Google Maps script"));
            };
            document.head.appendChild(script);
        });
    };

    const initializeMap = (centerCoords = formData.coordinates) => {
        if (!window.google || !window.google.maps || !window.google.maps.Map || !mapRef.current) {
            console.log(en_ja["Google Maps not ready or map container not available"], centerCoords);
            return;
        }
        try {
            if (mapInstance.current && markerInstance.current) {
                const latlng = new window.google.maps.LatLng(centerCoords.lat, centerCoords.lng);
                mapInstance.current.setCenter(latlng);
                markerInstance.current.setPosition(latlng);
                return;
            }
            const centerLatLng = new window.google.maps.LatLng(centerCoords.lat, centerCoords.lng);
            const map = new window.google.maps.Map(mapRef.current, {
                center: centerLatLng,
                zoom: 13,
            });
            const marker = new window.google.maps.Marker({
                position: centerLatLng,
                map,
                draggable: true,
            });
            map.addListener("click", (e) => {
                if (e.latLng) {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    marker.setPosition(e.latLng);
                    setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
                    setSelectedPlaceId(null);
                    if (geocoder.current) {
                        geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                                setFormData((prev) => ({ ...prev, address: results[0].formatted_address }));
                            }
                        });
                    }
                }
            });
            marker.addListener("dragend", (e) => {
                if (e.latLng) {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
                    setSelectedPlaceId(null);
                    if (geocoder.current) {
                        geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                                setFormData((prev) => ({ ...prev, address: results[0].formatted_address }));
                            }
                        });
                    }
                }
            });
            mapInstance.current = map;
            markerInstance.current = marker;
            if (window.google.maps.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                placesService.current = new window.google.maps.places.PlacesService(map);
            }
            geocoder.current = new window.google.maps.Geocoder();
            setTimeout(() => {
                if (mapInstance.current) {
                    window.google.maps.event.trigger(mapInstance.current, "resize");
                    mapInstance.current.setCenter(centerLatLng);
                }
            }, 300);
            console.log(en_ja["Map initialized successfully"]);
        } catch (error) {
            console.error(en_ja["Error initializing map:"], error);
            setMapError(en_ja["Failed to initialize map: "] + error.message);
        }
    };

    useEffect(() => {
        if (!dialogOpen) {
            if (markerInstance.current) {
                markerInstance.current.setMap(null);
                markerInstance.current = null;
            }
            if (mapInstance.current) {
                mapInstance.current = null;
            }
            setMapLoaded(false);
            setMapError("");
            return;
        }
        const initialCoords = editMode && storeData && storeData.latitude && storeData.longitude ? { lat: Number(storeData.latitude), lng: Number(storeData.longitude) } : formData.coordinates || DEFAULT_COORDS;
        if (editMode && storeData) {
            setFormData({
                company_name: storeData.company_name || "",
                store_name: storeData.store_name || "",
                address: storeData.address || "",
                prefectures: storeData.prefectures || "",
                postalCode: storeData.postalCode || "",
                from: storeData.from ? storeData.from.slice(0, 5) : "",
                to: storeData.to ? storeData.to.slice(0, 5) : "",
                status: storeData.status ?? true,
                created_by: storeData.created_by || userDetails?.id,
                coordinates: initialCoords,
            });
            setSelectedPlaceId(null);
        }
        const initializeGoogleMaps = async () => {
            try {
                await loadGoogleMaps();
                setTimeout(() => {
                    if (mapRef.current) {
                        initializeMap(initialCoords);
                    }
                }, 500);
            } catch (error) {
                console.error(en_ja["Failed to load Google Maps:"], error);
                setMapError(en_ja["Failed to load Google Maps. Please check your internet connection."]);
            }
        };
        const timeoutId = setTimeout(initializeGoogleMaps, 100);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [dialogOpen, editMode, storeData]);

    useEffect(() => {
        if (!mapInstance.current || !markerInstance.current || !formData.coordinates) return;
        try {
            const { lat, lng } = formData.coordinates;
            if (typeof lat === "number" && typeof lng === "number" && window.google && window.google.maps) {
                const latlng = new window.google.maps.LatLng(lat, lng);
                markerInstance.current.setPosition(latlng);
                mapInstance.current.setCenter(latlng);
            }
        } catch (error) {
            console.error(en_ja["Error updating map position:"], error);
        }
    }, [formData.coordinates]);

    const validateForm = () => {
        const newErrors = {};
        if (!(formData.company_name || "").trim()) newErrors.company_name = en_ja["Company name is required"];
        if (!(formData.store_name || "").trim()) newErrors.store_name = en_ja["Store name is required"];
        if (!(formData.address || "").trim()) newErrors.address = en_ja["Address is required"];
        if (!(formData.prefectures || "").trim()) newErrors.prefectures = en_ja["Prefectures is required"];
        if (!(formData.postalCode || "").trim()) newErrors.postalCode = en_ja["Postal code is required"];
        if (!(formData.from || "").trim()) newErrors.from = en_ja["Opening time is required"];
        if (!(formData.to || "").trim()) newErrors.to = en_ja["Closing time is required"];
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field) => (event) => {
        setFormData((prev) => ({ ...prev, [field]: event.target.value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
        if (field === "address") setSelectedPlaceId(null);
    };

    const handleStatusToggle = () => {
        setFormData(prev => ({ ...prev, status: !prev.status }));
    };

    const handleClose = () => {
        setFormData({
            company_name: '',
            store_name: '',
            address: '',
            prefectures: '',
            postalCode: '',
            from: '',
            to: '',
            status: true,
            created_by: userDetails?.id,
            coordinates: DEFAULT_COORDS,
        });
        setSelectedPlaceId(null);
        setErrors({});
        onClose();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        if (!formData.address.trim()) {
            setErrors((prev) => ({ ...prev, address: en_ja["Address is required"] }));
            return;
        }
        if (!geocoder.current) return;
        geocoder.current.geocode({ address: formData.address }, async (results, status) => {
            if (status !== "OK" || !results || results.length === 0) {
                setErrors((prev) => ({ ...prev, address: en_ja["Please enter a valid address"] }));
                return;
            }
            const location = results[0].geometry.location;
            const coords = { lat: location.lat(), lng: location.lng() };
            setFormData((prev) => ({ ...prev, coordinates: coords }));
            setLoading(true);
            const obj = {
                company_name: formData.company_name,
                store_name: formData.store_name,
                address: formData.address,
                prefectures: formData.prefectures,
                postalCode: formData.postalCode,
                from: formData.from,
                to: formData.to,
                status: formData.status,
                created_by: formData.created_by,
                latitude: coords.lat,
                longitude: coords.lng,
            };
            try {
                let response;
                if (editMode) {
                    response = await api.put(`/v1/admin/store/edit/${storeData.id}`, obj);
                } else {
                    response = await api.post("/v1/admin/add-store", obj);
                }
                if (response.status === 200 || response.status === 201) {
                    setSnackbarMessage(response?.data?.message || (editMode ? en_ja["Store updated successfully"] : en_ja["Store added successfully"]));
                    setSnackbarOpen(true);
                    setSnackbarType("success");
                    if (onSubmit) onSubmit();
                    handleClose();
                }
            } catch (error) {
                setSnackbarMessage(error?.response?.data?.error || error?.response?.data?.message || en_ja["Something went wrong."]);
                setSnackbarOpen(true);
                setSnackbarType("error");
            } finally {
                setLoading(false);
            }
        });
    };

    if (!dialogOpen) return null;

    return (
        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{editMode ? en_ja["Edit Store"] : en_ja["Add New Store"]}</Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, height: "70vh", display: "flex" }}>
                <Box sx={{ width: 400, px: 3, borderRight: 1, borderColor: "divider" }}>
                    <TextField
                        fullWidth
                        label={en_ja["Company Name"]}
                        value={formData.company_name}
                        onChange={handleInputChange("company_name")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.company_name}
                        helperText={errors.company_name}
                    />
                    <TextField
                        fullWidth
                        label={en_ja["Store Name"]}
                        value={formData.store_name}
                        onChange={handleInputChange("store_name")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.store_name}
                        helperText={errors.store_name}
                    />
                    <Autocomplete
                        freeSolo
                        options={predictions.map((p) => ({ label: p.description, placeId: p.place_id }))}
                        value={formData.address || ""}
                        onInputChange={(event, value) => {
                            setFormData((prev) => ({ ...prev, address: value }));
                            setErrors((prev) => ({ ...prev, address: "" }));
                            setSelectedPlaceId(null);
                            if (!value || !autocompleteService.current) {
                                setPredictions([]);
                                return;
                            }
                            autocompleteService.current.getPlacePredictions({ input: value }, (results) => {
                                setPredictions(results || []);
                            });
                        }}
                        onChange={(event, newValue) => {
                            if (typeof newValue === "string") {
                                setFormData((prev) => ({ ...prev, address: newValue }));
                                setSelectedPlaceId(null);
                                setErrors((prev) => ({ ...prev, address: "" }));
                            } else if (newValue && newValue.placeId) {
                                handleSelectPrediction(newValue.placeId, newValue.label);
                            } else {
                                setFormData((prev) => ({ ...prev, address: "" }));
                                setSelectedPlaceId(null);
                            }
                            setPredictions([]);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={en_ja["Address"]}
                                error={!!errors.address}
                                helperText={errors.address}
                                margin="normal"
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        label={en_ja["Prefectures"]}
                        value={formData.prefectures}
                        onChange={handleInputChange("prefectures")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.prefectures}
                        helperText={errors.prefectures}
                    />
                    <TextField
                        fullWidth
                        label={en_ja["Post Code"]}
                        value={formData.postalCode}
                        onChange={handleInputChange("postalCode")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.postalCode}
                        helperText={errors.postalCode}
                    />
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        {en_ja["Operating Hours"]}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <TextField
                            label={en_ja["From"]}
                            type="time"
                            value={formData.from}
                            onChange={handleInputChange("from")}
                            error={!!errors.from}
                            helperText={errors.from}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label={en_ja["To"]}
                            type="time"
                            value={formData.to}
                            onChange={handleInputChange("to")}
                            error={!!errors.to}
                            helperText={errors.to}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <FormControlLabel
                            label={<Typography variant="subtitle2" fontWeight="bold">{en_ja["Active Status"]}</Typography>}
                            control={<Switch checked={formData.status} onChange={handleStatusToggle} color="success" />}
                        />
                    </Box>
                </Box>

                <Box sx={{ flex: 1, position: "relative" }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="body2" color="text.secondary">
                            {en_ja["Store Location Map"]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {en_ja["Interactive map will show here. Drag pin to set exact location"]}
                        </Typography>
                    </Box>
                    <Box sx={{ height: "calc(100% - 60px)", position: "relative" }}>
                        {mapError ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column" }}>
                                <Typography color="error" gutterBottom>{mapError}</Typography>
                                <Button onClick={() => { setMapError(""); loadGoogleMaps().then(() => initializeMap(formData.coordinates)); }}>{en_ja["Retry"]}</Button>
                            </Box>
                        ) : !mapLoaded ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                <Typography>{en_ja["Loading Google Maps..."]}</Typography>
                            </Box>
                        ) : (
                            <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                        )}
                        <Box sx={{ position: "absolute", bottom: 16, right: 16, bgcolor: "background.paper", p: 1, borderRadius: 1, boxShadow: 1, fontSize: "0.75rem" }}>
                            <strong>{en_ja["Coordinates"]}:</strong><br />
                            {formData.coordinates.lat.toFixed(6)}, {formData.coordinates.lng.toFixed(6)}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} variant="outlined" disabled={loading}>{en_ja["Cancel"]}</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} variant="contained" disabled={loading} sx={{ backgroundColor: "rgba(220, 53, 69, 1)", color: "white" }}>
                    {editMode ? en_ja["Update Store"] : en_ja["Create Store"]}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default AddStore;
